import { addUUIDsToArray } from '@/utils/uuidHelper';
import uuidGenerator from '@/utils/uuidGenerator';
import { dictionaryDateTypes, dictionaryFieldTypes, dictionaryNumberTypes } from '@/constants/fields';
import { pruneEntityErrors, pruneFieldError } from '@/utils/errors';
import parseNumericValue, { numberRangeValues, numbersValues } from '@/utils/numberHelper';
import { removeEmpty } from '@/utils/resultsHelper';
import {
  cohortDateTimeValue,
  cohortDateValue,
  cohortTimeValue,
  parseDateToDateString,
  parseDateToDateTimeString,
  parseDateToTimeTzString,
} from '../datetimeHelper';
import {
  cohortArrayOperators,
  cohortExistOperators,
  cohortJoinOperators,
  cohortRangeOperators,
} from '../constants';

export const newCohortFilterField = ({
  type, name, constraints, cohort_filter: cohortFilter,
}) => ({
  type, name, constraints, cohort_filter: cohortFilter,
});

export const newCohortFilter = (
  field = newCohortFilterField({
    type: null, name: null, constraints: null, cohort_filter: true,
  }),
  operator = null,
  values = [],
  isNew = true,
) => (
  {
    uuid: uuidGenerator.generate(),
    operator,
    field: { ...field },
    values,
    isNew,
  }
);

const addUUIDsToCohortFilters = (clause, addPlaceHolder = false, removeId = false) => {
  let filters = addUUIDsToArray(clause.filters);
  if (removeId) {
    filters = filters.map((filter) => {
      const newFilter = { ...filter };
      delete newFilter.id;
      return newFilter;
    });
  }

  if (addPlaceHolder) {
    filters.push(newCohortFilter());
  }
  return filters;
};

const numberValues = (operator, fieldType, values) => {
  if (Object.values(cohortRangeOperators).includes(operator)) {
    return numberRangeValues(values, '', fieldType);
  }

  if (Object.values(cohortArrayOperators).includes(operator)) {
    return numbersValues(values, '', fieldType);
  }

  return [parseNumericValue(values[0], '', fieldType)];
};

const defaultDateValues = (operator, values) => {
  const newValues = [cohortDateValue(values[0], null)];

  if (Object.values(cohortRangeOperators).includes(operator)) {
    newValues[1] = cohortDateValue(values[1], null);
  }

  return removeEmpty(newValues).map((v) => parseDateToDateString(v));
};

const defaultDateTimeValues = (operator, values) => {
  const newValues = [cohortDateTimeValue(values[0], null)];

  if (Object.values(cohortRangeOperators).includes(operator)) {
    newValues[1] = cohortDateTimeValue(values[1], null);
  }

  return removeEmpty(newValues).map((v) => parseDateToDateTimeString(v));
};

const defaultTimeValues = (operator, values) => {
  const newValues = [cohortTimeValue(values[0], null)];

  if (Object.values(cohortRangeOperators).includes(operator)) {
    newValues[1] = cohortTimeValue(values[1], null);
  }

  return removeEmpty(newValues).map((v) => parseDateToTimeTzString(v));
};

const datetimeValues = (operator, fieldType, values) => {
  if (fieldType === dictionaryDateTypes.DATE) {
    return defaultDateValues(operator, values);
  }
  if (fieldType === dictionaryDateTypes.DATETIME) {
    return defaultDateTimeValues(operator, values);
  }
  if (fieldType === dictionaryDateTypes.TIME) {
    return defaultTimeValues(operator, values);
  }
  return values;
};

export const defaultFilterValues = (operator, fieldType, values = []) => {
  if (Object.values(cohortExistOperators).includes(operator)
        || Object.values(cohortJoinOperators).includes(operator)) {
    return [];
  }
  if (Object.values(dictionaryNumberTypes).includes(fieldType)) {
    return numberValues(operator, fieldType, values);
  }
  if (Object.values(dictionaryDateTypes).includes(fieldType)) {
    return datetimeValues(operator, fieldType, values);
  }
  return values;
};

export const newCohortFilterJoin = ({ type, name }, { code }) => ({ type, name, dictionary: { code } });

export const pruneCohortFilterErrors = (errors, { filterUUID }, field = null) => (
  pruneEntityErrors(errors, ['filter'], (e) => e.context.filterUUID !== filterUUID
        || (e.context.filterUUID === filterUUID && pruneFieldError(e, field)))
);

const cloneJoin = ({ join }) => ({
  name: join.name,
  type: join.type,
  dictionary: { code: join.dictionary?.code },
});

const removeUUIDsFromCohortFilter = (filter) => {
  const field = filter.field
      || newCohortFilterField({
        type: dictionaryFieldTypes.TEXT, name: null, constraints: null, cohort_filter: true,
      });
  const {
    name, type, constraints, cohort_filter: cohortFilter,
  } = field;
  const copy = {
    operator: filter.operator,
    field: {
      name, type, constraints, cohort_filter: cohortFilter,
    },
    values: null,
  };

  if (Object.values(cohortJoinOperators).includes(filter.operator)) {
    if (filter.join) { copy.join = cloneJoin(filter); }
  } else if (!Object.values(cohortExistOperators).includes(filter.operator)) {
    copy.values = [...filter.values];
  }

  return copy;
};

export const removeUUIDsFromCohortFilters = (filters) => (
  filters.filter((f) => !f.isNew).map(removeUUIDsFromCohortFilter)
);

export default addUUIDsToCohortFilters;
